/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useDispatch, useSelector, batch } from 'react-redux';
import getCurrencySymbol from 'currency-symbol-map';
import { Dropdown } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { FormPaymentFieldIndicator, PayButton, UiIndicator } from '../../common-field-styles';
import PaymentModalButton from '../../common-field-components/payment-modal-button';
import { actionTypes, getInitiatState, stripeStateReducer } from './stripe-reducer';
import { confirmPayment, setUserInputOnCtrl } from '../../../form/reducer/form';
import { getStripeProducts, payCharge, paySubscription } from './stripe-utils';
import { onPaymentValueChange } from '../../../form/slice-actions/form';
import { FormContext } from '../../../shared/contexts/form-context';
import { tryParseJSONObject } from '../../../../shared/utils';
import { toHex, getLabel } from '../../../../shared/form-lib';
import StripeCheckoutForm from './stripe-checkout-form';
import UserSpecifyForm from './stripe-useramount-form';
import { ControlPropType } from '../../ctrl_proptype';
import win from '../../../../shared/win';
import { Field } from './stripe.styled';

const StripeSubComponent = ({ ctrl, formDesign, isRenderedOnModal }) => {
  const { inForm, currentPageNo } = useContext(FormContext);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const errorTimerRef = useRef(null);
  const {
    payments,
    ctrlIndex,
    isPayOnSubmission,
    userAmount: calcUserAmount,
    editableForm: { form_options, definition, stripe_public_key, user_custom_texts, respondent_email },
  } = useSelector((state) => state.form);
  const { isPaid, isError, message, resMessage } = payments[ctrl.type][ctrl.internal_id] || {};
  const modeStatusText = !form_options?.stripe_live_mode ? '(test mode)' : '';
  const submitButtonText =
    inForm && (formDesign.old.submission_button_text || getLabel('submit_text', user_custom_texts) || 'Submit');
  const [planDetails, setPlanDetails] = useState({
    options: [],
    images: [],
    subscriptionItems: [],
  });

  const initialState = getInitiatState({ ctrl, respondentEmail: respondent_email });
  const [stripeContext, contextDispatch] = useReducer(stripeStateReducer, initialState);
  const {
    paying,
    finalAmount,
    storeCardInfo,
    elementContent,
    showCheckoutForm,
    showUserAmountForm,

    selectedSubscriptionProduct,
    selectedSubscriptionProductDetails,
    loadingSubscriptionProducts,
    loadingSubscriptionProductsError,
    productError,

    disableInputs,
    userAmount,
    lineItem,
    formData,
  } = stripeContext;
  const { OTHERS, GROUPED } = actionTypes;

  const updateState = (type, payload) => {
    contextDispatch({
      type,
      payload,
    });
  };

  const handleStorePaymentValue = (status, displayMsg, resMsg, newValue, transactionId = '') => {
    batch(() => {
      if (status === 'paid') {
        dispatch(
          setUserInputOnCtrl({
            newValue,
            property: 'value',
            page: currentPageNo - 1,
            ctrlId: ctrl.internal_id,
            isValid: true,
          })
        );

        if (ctrl.value !== newValue) {
          dispatch(onPaymentValueChange());
        }
      }

      dispatch(
        confirmPayment({
          type: ctrl.type,
          isPaid: status === 'paid',
          isError: status === 'error',
          message: displayMsg,
          resMessage: resMsg,
          ctrlId: ctrl.internal_id,
          transactionId,
        })
      );
    });
  };

  useEffect(() => {
    if (inForm) {
      const abortController = new AbortController();
      if (ctrl.props.list_products && !isPaid) {
        getStripeProducts(ctrl, setPlanDetails, updateState);
      }
      return () => {
        abortController.abort();
        // stop the query by aborting on the AbortController on unmount
      };
    }
  }, [inForm, ctrl, isPaid]);

  useEffect(() => {
    if (productError) {
      errorTimerRef.current = setTimeout(() => {
        updateState(OTHERS, { productError: '' });
      }, 3500);
    }
    return () => clearTimeout(errorTimerRef.current);
  }, [OTHERS, productError]);

  useEffect(() => {
    if (isPaid && !paying && !isError) {
      // Restore blank form after payment
      updateState(GROUPED, {
        ref: 'formData',
        main: { name: '', email: '', line1: '', city: '', state: '', postal_code: '', country: '' },
      });
    }

    // Enforce users to edit/commplete their card details again.. !IMPORTANT
    if (isError) {
      updateState(GROUPED, { ref: 'elementContent', main: { complete: false } });
    }
  }, [isError, paying, isPaid, GROUPED]);

  // Get details of selected subscription product
  useEffect(() => {
    if (ctrl.props.list_products && selectedSubscriptionProduct) {
      const item = planDetails.subscriptionItems[planDetails.options.indexOf(selectedSubscriptionProduct)];
      updateState(OTHERS, {
        finalAmount: planDetails.subscriptionItems ? item?.amount : 0,
        selectedSubscriptionProductDetails: item,
      });
    }
  }, [ctrl.props.list_products, selectedSubscriptionProduct, OTHERS, planDetails]);

  // Dynamic input/formData update using target name
  const handleInputChange = ({ target: { name, value } }) => {
    updateState(GROUPED, { ref: 'formData', main: { [name]: value } });
  };

  // Handle save card check box state
  const handleCheckSaveCard = () => {
    updateState(OTHERS, { storeCardInfo: !stripeContext.storeCardInfo });
  };

  // Gather additional customer data we may have collected in our form.
  const getAdditionalFormData = () => {
    // Used in the creation of token
    const additionalData = {
      line1: formData.line1 || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      postal_code: formData.postal_code || undefined,
      country: formData.country || undefined,
    };

    // Used in the creation of charge and/or subscription
    const paymentDetails = {
      name: formData.name || undefined,
      email: formData.email || undefined,
      amount: lineItem.amount,
      currency: lineItem.currency,
      description: lineItem.description,
      save_card: storeCardInfo,
    };

    return {
      paymentDetails,
      additionalData,
    };
  };

  // Fetch additional information from fields added as metadata
  const getControlMetadata = () => {
    const metadata = {
      'Form Name': definition.name,
      'Form Link': window.location.href,
    };

    if (!isEmpty(ctrlIndex)) {
      for (let m = 0; m < ctrl.props.metadata.length; m += 1) {
        const mdata = ctrl.props.metadata[m];
        if (mdata.value && ctrlIndex[mdata.value].value) {
          metadata[mdata.key] = ctrlIndex[mdata.value].value?.toString().substring(0, 250);
        } else {
          metadata[mdata.key] = null;
        }
      }
    }
    return metadata;
  };

  const scrollToCardElement = () => {
    const stripeElementRef = document.getElementById(`{stripe_card_element-${ctrl.internal_id}}`);
    stripeElementRef.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const paymentFormRef = document.getElementById(`{stripe_paymentform-${ctrl.internal_id}}`);
    const cardErrorRef = document.getElementById(`{stripe_card_error-${ctrl.internal_id}}`);

    const closeModal = () => {
      updateState(OTHERS, {
        showCheckoutForm: false,
      });
    };

    // Prevent submission if stripe elements is absent
    if (elements === null) return;

    // Prevent submission if card info is blank
    if (isEmpty(elementContent) || elementContent.empty) {
      cardErrorRef.textContent = 'Please fill in your card details';
      scrollToCardElement();
      return;
    }

    // Prevent submission if card info has error or is incomplete
    if (elementContent.error || !elementContent.complete) {
      cardErrorRef.textContent = elementContent.error
        ? elementContent.error.message
        : 'Please complete your card details';
      scrollToCardElement();
      return;
    }

    // Prevent submit if HTML5 validation fails
    if (paymentFormRef.reportValidity() === false) return;

    // Temporarily disable inputs fields while paying to avoid tampering with
    updateState(OTHERS, {
      disableInputs: true,
      paying: true,
    });

    const { additionalData, paymentDetails } = getAdditionalFormData();
    const metadata = getControlMetadata();
    const stripeCardElement = elements.getElement(CardElement);

    const payPayload = {
      ctrl,
      stripe,
      stripeCardElement,
      updateState,
      additionalData,
      paymentDetails,
      metadata,
      handleStorePaymentValue,
      stripeContext,
      modeStatusText,
      submitButtonText,
    };

    if (ctrl.props.list_products) {
      const isFunctionDone = await paySubscription({ ...payPayload, selectedSubscriptionProductDetails });
      if (isFunctionDone) {
        closeModal();
      }
    } else {
      const isFunctionDone = await payCharge({ ...payPayload });
      if (isFunctionDone) {
        closeModal();
      }
    }
  };

  // Show card info error as user types card details
  const handleCardElement = (ev) => {
    const cardErrorRef = document.getElementById(`{stripe_card_error-${ctrl.internal_id}}`);
    updateState(GROUPED, { ref: 'elementContent', main: { ...ev } });
    if (ev.error) {
      cardErrorRef.textContent = ev.error.message;
    } else {
      cardErrorRef.textContent = '';
    }
  };

  function getSelectedSubscription(d, dd) {
    if (dd) {
      updateState(OTHERS, { selectedSubscriptionProduct: d.value });
    } else {
      updateState(OTHERS, { selectedSubscriptionProduct: d.target.value });
    }
  }

  const startPaymentForm = () => {
    // Prevent start payment if not atleast on professional plan
    if (!win.PLAN.has_payment) {
      updateState(OTHERS, { productError: 'Upgrade to Professional plan to make use of payment.' });
      return;
    }

    if (showUserAmountForm) updateState(OTHERS, { showUserAmountForm: false });
    if (!showCheckoutForm) updateState(OTHERS, { showCheckoutForm: true });

    let amount;
    if (ctrl.props.user_specify) {
      amount = Math.round(userAmount * ctrl.props.sub_unit);
      updateState(GROUPED, {
        ref: 'lineItem',
        main: {
          amount,
          currency: ctrl.props.currency,
          name: 'Total amount to be paid',
          description: ctrl.props.label,
        },
      });
    } else {
      amount = Number(calcUserAmount[ctrl.internal_id] ?? ctrl.props.amount);
      amount = Math.round(amount * ctrl.props.sub_unit);
      updateState(GROUPED, {
        ref: 'lineItem',
        main: {
          amount,
          currency: ctrl.props.currency,
          name: ctrl.props.label,
          description: ctrl.props.label,
        },
      });
    }

    // This to ensure the amount is only updated on startPayment
    // If no products or no subscription is chosen
    if (!ctrl.props.list_products && !selectedSubscriptionProduct) {
      updateState(OTHERS, { finalAmount: amount });
    }

    if (ctrl.props.list_products) {
      if (!win.PLAN.has_stripe_subscription) {
        updateState(OTHERS, { productError: 'Upgrade to Professional plan to make use of subscription.' });
        return;
      }

      // Prevent payment form, if not selected any product in list
      if (isEmpty(selectedSubscriptionProductDetails)) {
        updateState(OTHERS, {
          showCheckoutForm: false,
          productError: 'Select a product before continuing.',
        });
      }
    }
  };

  const noStripePublicKey = () =>
    tryParseJSONObject(stripe_public_key) === false ? !stripe_public_key : isEmpty(JSON.parse(stripe_public_key));

  // Show user specify form or start payment form instantly
  const checkIfUserSpecify = () => {
    if (noStripePublicKey()) return;
    if (ctrl.props.user_specify) {
      updateState(OTHERS, { showUserAmountForm: true });
    } else {
      startPaymentForm();
    }
  };

  return (
    <>
      {isRenderedOnModal ? (
        <PaymentModalButton
          errorMessage={isError ? resMessage || 'An error occured while paying.' : ''}
          isTestMode={inForm && !form_options?.stripe_live_mode}
          isNoKeyAvailable={inForm && noStripePublicKey()}
          onClick={() => inForm && checkIfUserSpecify()}
          isCompleted={!!(isPaid && ctrl.value)}
          amount={ctrl.props.amount}
          ctrl={ctrl}
        />
      ) : (
        <>
          {isPayOnSubmission ? (
            <FormPaymentFieldIndicator>
              There is a {ctrl.props.label} payment field attached to this form. You may be required to make payment of{' '}
              {`${getCurrencySymbol(ctrl.props.currency || 'USD')}${finalAmount}`} after submission.
            </FormPaymentFieldIndicator>
          ) : (
            <>
              {ctrl.props.list_products &&
                !isPaid &&
                !paying &&
                !loadingSubscriptionProducts &&
                !loadingSubscriptionProductsError &&
                !ctrl.value && (
                  <div>
                    {ctrl.props.field_type === 'radio_buttons' &&
                      inForm &&
                      planDetails.options?.map((option, index) => (
                        <Field key={index}>
                          <div id={`fpl-${ctrl.internal_id}`} className="ui radio checkbox">
                            <input
                              type="radio"
                              value={option}
                              name={ctrl._name}
                              disabled={!inForm}
                              id={`${ctrl._name}-${index}`}
                              title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
                              checked={selectedSubscriptionProduct === option}
                              onChange={getSelectedSubscription}
                            />
                            <label
                              htmlFor={`${ctrl._name}-${index}`}
                              style={{ color: `${toHex(formDesign.old.form_text_color)}` }}>
                              {option}
                            </label>
                          </div>
                        </Field>
                      ))}

                    {ctrl.props.field_type === 'select_basic' && inForm && (
                      <Dropdown
                        selection
                        search
                        disabled={!inForm}
                        options={planDetails.options?.map((option, index) => ({
                          key: index,
                          text: option,
                          value: option,
                        }))}
                        onChange={(e, d) => getSelectedSubscription(d, 'dd')}
                        value={selectedSubscriptionProduct}
                      />
                    )}

                    {ctrl.props.field_type === 'image_choice' &&
                      inForm &&
                      planDetails.options?.map((option, index) => (
                        <div key={index} style={{ marginTop: '20px' }}>
                          <div>{option}</div>
                          <label>
                            <input
                              type="radio"
                              value={option}
                              name={`${ctrl.internal_id}-${index}`}
                              disabled={!inForm}
                              checked={selectedSubscriptionProduct === option}
                              onChange={getSelectedSubscription}
                            />
                            <div>
                              <img src={planDetails.images[index]} alt="" height={100} />
                            </div>
                          </label>
                        </div>
                      ))}
                  </div>
                )}

              {inForm && !isPaid && ctrl.props.list_products && loadingSubscriptionProducts && (
                <div>
                  <div className="inline ui active tiny loader" /> Loading products...
                </div>
              )}

              {(!isPaid || !paying) && !loadingSubscriptionProducts && loadingSubscriptionProductsError && (
                <UiIndicator className="status red">
                  <span>{loadingSubscriptionProductsError || 'An error occured while loading stripe products.'}</span>
                </UiIndicator>
              )}

              {(isPaid || ctrl.value) && (
                <UiIndicator className="paid">
                  <i className="check icon" />
                  <span>{message || ctrl.value}</span>
                </UiIndicator>
              )}

              {/* Messsage show when successful payment is made from calling the payment APIs */}
              {isPaid && resMessage && (
                <UiIndicator className="status green">
                  <span>{resMessage}</span>
                </UiIndicator>
              )}

              {/* Messsage show when error is returned from calling the payment APIs */}
              {isError && (
                <UiIndicator className="status red">
                  <span>{resMessage || 'An error occured while paying.'}</span>
                </UiIndicator>
              )}

              {/* Error show when pay button is clicked without selecting a product in product list */}
              {productError && (
                <UiIndicator className="status red">
                  <span>{productError}</span>
                </UiIndicator>
              )}

              {/* To show error when no stripe_public_key test/live mode */}
              {inForm && noStripePublicKey() && (
                <UiIndicator className="status red">
                  <span>
                    The form owner has not set up their payment integration. Please contact the owner for more
                    information.
                  </span>
                </UiIndicator>
              )}

              {!isPaid && !ctrl.value && (
                <PayButton
                  onClick={() => inForm && checkIfUserSpecify()}
                  id={`fpl-${ctrl.internal_id}`}
                  disabled={paying}
                  bgColor="#3e2a89"
                  type="button">
                  {ctrl.props.pay_button_text}
                </PayButton>
              )}

              {paying && <div className="inline ui active tiny loader" />}

              {inForm && !form_options.stripe_live_mode && <UiIndicator className="mode">Test mode</UiIndicator>}
            </>
          )}
        </>
      )}

      {/* Main payment or subscription form */}
      {inForm && (
        <StripeCheckoutForm
          ctrl={ctrl}
          formDesign={formDesign}
          lineItem={lineItem}
          stripe={stripe}
          isPaying={paying}
          isError={isError}
          elements={elements}
          finalAmount={finalAmount}
          formData={formData}
          updateState={updateState}
          showCheckoutForm={showCheckoutForm}
          disableInputs={disableInputs}
          selectedSubscriptionProductDetails={selectedSubscriptionProductDetails}
          handleInputChange={handleInputChange}
          handleFormSubmit={handleFormSubmit}
          storeCardInfo={storeCardInfo}
          handleCheckSaveCard={handleCheckSaveCard}
          handleCardElement={handleCardElement}
        />
      )}

      {/* User amount specify form */}
      {inForm && (
        <UserSpecifyForm
          formDesign={formDesign}
          ctrl={ctrl}
          showUserAmountForm={showUserAmountForm}
          updateState={updateState}
          userAmount={userAmount}
          startPaymentForm={startPaymentForm}
        />
      )}
    </>
  );
};

StripeSubComponent.defaultProps = {
  isRenderedOnModal: false,
  formDesign: null,
};

StripeSubComponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  isRenderedOnModal: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};

export default StripeSubComponent;
